import { Button, Col, Drawer, Form, Input, InputNumber, Row, Select, notification, Upload, Modal } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { Option } from 'antd/es/mentions';
import { useEffect, useState } from 'react';
import { ActionType } from '../../constants/actionType';
import { createEpisodeIntro, updateEpisodeIntro } from '../../helpers/api/story';
import type { UploadFile } from 'antd/es/upload/interface';
import { RcFile, UploadProps } from 'antd/es/upload';
import { Icon } from 'ts-react-feather-icons';
import { EpisodeIntroModel } from '../../model/EpisodeIntro';
import './EpisodeIntroDrawer.scss';

const episodeIntroDrawerValidation = {
    type: [
        { required: true, message: 'Type is required' },
    ],
    index: [
        { required: true, message: 'Index is required' },
    ],
    text: [
        { max: 1000, message: 'Text must be less than 1000 characters' },
    ],
}

const EpisodeIntroDrawer = ({
    episode,
    drawerVisibility,
    setdDrawerVisibility,
    introDetail,
    setIntroDetail,
    actionType,
    setActionType,
    onSuccess,
    onFailure,
    existingIntros = []
}: any) => {
    const [form] = Form.useForm();
    const formValueObj = Form.useWatch([], form);

    const [submittables, setSubmittable] = useState<boolean>(false);
    const [loadingBtn, setLoadingBtn] = useState<boolean>(false);
    const [type, setType] = useState<string>("IMAGE_WITH_TEXT");
    
    // Media upload states
    const [media, setMedia] = useState<UploadFile[]>([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewMedia, setPreviewMedia] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    const [api, contextHolder] = notification.useNotification();
    
    // Calculate next index based on existing intros
    const getNextIndex = () => {
        if (existingIntros.length === 0) return 1;
        const maxIndex = Math.max(...existingIntros.map((intro: any) => intro.index));
        return maxIndex + 1;
    };

    // Custom validation function for media
    const validateMedia = () => {
        if (media.length === 0) {
            return Promise.reject(new Error('Media is required'));
        }
        return Promise.resolve();
    };

    // Media preview functions
    const handleMediaCancel = () => setPreviewOpen(false);

    const handleMediaPreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as RcFile);
        }

        setPreviewMedia(file.url || (file.preview as string));
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
    };

    const getBase64 = (file: RcFile): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });

    const handleMediaChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
        setMedia(newFileList);
        // Trigger validation
        setTimeout(() => {
            form.validateFields(['media']);
        }, 100);
    };

    const mediaUploadButton = (
        <div>
            <Icon name="plus" size={20} />
            <div style={{ marginTop: 8 }}>Upload Media</div>
        </div>
    );

    const showError = (errorMessage: string) => {
        api.error({
            message: `Error notification`,
            description: <span className="text-danger">{errorMessage || 'There\'re some errors occurred!'}</span>,
            placement: 'topRight'
        });
    }

    useEffect(() => {
        const values = form.getFieldsValue();
        const hasValues = Object.keys(values).some(key => {
            if (key === 'text' && type === 'VIDEO') return false;
            return values[key] !== undefined && values[key] !== null && values[key] !== '';
        });
        setSubmittable(hasValues && media.length > 0);
    }, [formValueObj, media, type]);

    useEffect(() => {
        if (!introDetail) {
            form.setFieldValue("type", "IMAGE_WITH_TEXT");
            form.setFieldValue("index", getNextIndex());
            form.setFieldValue("text", "");
            setMedia([]);
            setType("IMAGE_WITH_TEXT");
            return;
        }
        setType(introDetail.type);
        
        // Handle media default for existing intros
        if (introDetail.mediaUrl && introDetail.mediaUrl.trim().length > 0) {
            const urlTemp = introDetail.mediaUrl.split("/");
            setMedia([{
                uid: "1",
                name: urlTemp[urlTemp.length - 1],
                status: 'done',
                url: introDetail.mediaUrl
            }]);
        } else {
            setMedia([]);
        }
        
        form.setFieldsValue({ ...introDetail });
    }, [introDetail, existingIntros]);

    const onClose = () => {
        form.resetFields();
        setIntroDetail(null);
        form.setFieldValue("type", "IMAGE_WITH_TEXT");
        form.setFieldValue("index", getNextIndex());
        form.setFieldValue("text", "");
        setMedia([]);
        setSubmittable(false);
        setdDrawerVisibility(false);
        setActionType("");
        setType("IMAGE_WITH_TEXT");
    }

    const saveIntro = async () => {
        try {
            setLoadingBtn(true);
            await form.validateFields();
            
            const requestModel: any = {
                type: type,
                text: type === 'IMAGE_WITH_TEXT' ? formValueObj.text?.trim() : null,
                index: formValueObj.index || 1
            };

            let response;
            if (actionType === ActionType.CREATE) {
                // For create, always include media if available
                const requestBody: any = {
                    data: JSON.stringify(requestModel)
                };
                if (media.length > 0 && media[0].originFileObj) {
                    requestBody.media = media[0].originFileObj;
                }
                response = await createEpisodeIntro(episode.id, requestBody);
            } else {
                // For update, check if there's a new file
                const requestBody: any = {
                    data: JSON.stringify(requestModel)
                };
                
                if (media.length > 0) {
                    if (media[0].url === introDetail.mediaUrl) {
                        // Keep existing media
                        requestModel.mediaUrl = introDetail.mediaUrl;
                        requestBody.data = JSON.stringify(requestModel);
                    } else {
                        // Upload new media
                        requestBody.media = media[0].originFileObj;
                    }
                }
                response = await updateEpisodeIntro(episode.id, introDetail.id, requestBody);
            }

            if (response.status === 200) {
                onSuccess();
                onClose();
            }
        } catch (err) {
            console.error(err);
            showError(err as string);
        } finally {
            setLoadingBtn(false);
        }
    }

    return (
        <Drawer
            title={(actionType === ActionType.UPDATE ? "Update" : "Create") + " Episode Intro"}
            className='episode-intro-drawer'
            width={750}
            onClose={() => { setdDrawerVisibility(false) }}
            open={drawerVisibility}
            closeIcon={false}
            footer={
                <div className='d-flex justify-content-center'>
                    <Button onClick={saveIntro} type="primary" className='me-3' disabled={!submittables} loading={loadingBtn}>
                        Save
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </div>
            }
        >
            {contextHolder}
            <Form layout="vertical" autoComplete='off' form={form}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="type"
                            label="Type"
                            rules={episodeIntroDrawerValidation.type}
                        >
                            <Select 
                                placeholder="Please select type" 
                                value={type} 
                                onChange={(value) => {
                                    setType(value);
                                    form.setFieldValue("type", value);
                                    if (value === "VIDEO") {
                                        form.setFieldValue("text", "");
                                    }
                                }}
                            >
                                <Option value="VIDEO">Video</Option>
                                <Option value="IMAGE_WITH_TEXT">Image with Text</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="index"
                            label="Order Index"
                            rules={episodeIntroDrawerValidation.index}
                        >
                            <InputNumber placeholder="Please enter order index" min={1} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="media"
                            label="Media"
                            help="Upload a video or image file"
                            rules={[
                                {
                                    validator: validateMedia
                                }
                            ]}
                        >
                            <Upload
                                className="episode-intro-media-uploader"
                                listType="picture-card"
                                fileList={media}
                                onPreview={handleMediaPreview}
                                onChange={handleMediaChange}
                                multiple={false}
                                accept="video/*,image/*"
                                beforeUpload={() => false}
                            >
                                {!media.length && mediaUploadButton}
                            </Upload>
                            <Modal
                                open={previewOpen}
                                title={previewTitle}
                                footer={null}
                                onCancel={handleMediaCancel}
                                width={600}
                            >
                                {type === 'VIDEO' ? (
                                    <video
                                        controls
                                        style={{width: '100%'}}
                                        src={previewMedia}
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    <img
                                        alt="preview"
                                        style={{width: '100%'}}
                                        src={previewMedia}
                                    />
                                )}
                            </Modal>
                        </Form.Item>
                    </Col>
                </Row>

                {type === 'IMAGE_WITH_TEXT' && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="text"
                                label="Text"
                                rules={episodeIntroDrawerValidation.text}
                            >
                                <TextArea rows={4} placeholder="Enter text to display with image" maxLength={1000} />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
            </Form>
        </Drawer>
    )
}

export default EpisodeIntroDrawer; 